import { FocusTrap } from 'focus-trap-react';
import { FC, JSX, PropsWithChildren, useEffect, useMemo, useRef, useState } from 'react';

import { AlignItemsProps } from 'app/components/complex/DatePickerFilter';

import styles, { BorderRadius } from '../../constants/styles';
import { ResizeObserverDimensions, useLockBodyScroll, useResizeObserver } from '../../hooks';
import Box from '../Box';
import ClickAwayListener from '../EventListeners/ClickAwayListener';
import Flex from '../Flex';

// Local Typings
export type DialogSize = 'auto' | 'lg' | 'md' | 'minContent' | 'sm' | 'xl' | 'xxl' | 'xxxl';
interface ClickAwayProps {
  onClickOutside: () => void;
}
interface Props {
  alignItems?: AlignItemsProps;
  borderRadius?: BorderRadius;
  isActive?: boolean;
  isFull?: boolean;
  isFullScreen?: boolean;
  isOpen: boolean;
  opacity: number;
  overflowYVisible?: boolean;
  size: DialogSize;
  top: number;
  transition: number;
  withTransition?: boolean;
}
interface ContainerProps {
  // eslint-disable-next-line @typescript-eslint/naming-convention
  CloseButton?: JSX.Element;
}

type NumericDialogSizes = Record<Exclude<DialogSize, 'minContent'>, number | undefined>;

type StringDialogSizes = Record<'minContent', 'min-content'>;

// Local Variables
const dialogSizes: NumericDialogSizes & StringDialogSizes = {
  auto: undefined,
  lg: 478,
  md: 440,
  minContent: 'min-content',
  sm: 300,
  xl: 700,
  xxl: 800,
  xxxl: 992,
};

// Component Definition
const Container: FC<PropsWithChildren<ClickAwayProps & ContainerProps & Props>> = ({
  alignItems = 'center',
  borderRadius = 'md',
  isActive: active,
  isOpen,
  onClickOutside,
  ...props
}) => {
  const ref = useRef<HTMLDivElement>(null);

  const [isTransitioning, setIsTransitioning] = useState(false);

  const [dimensions, setDimensions] = useState<ResizeObserverDimensions>({
    height: 30,
    width: 30,
  });

  useResizeObserver(ref,
    setDimensions);

  const maxWidth = useMemo(() => {
    if (props.withTransition && dimensions.width) {
      return undefined;
    }

    return props.isFull || props.isFullScreen ? '100%' : dialogSizes[props.size];
  },
  [dimensions.width, props.isFull, props.isFullScreen, props.size, props.withTransition]);

  const maxHeight = useMemo(() => {
    if (props.withTransition && dimensions.height) {
      return `${dimensions.height}px`;
    }

    return props.isFullScreen ? '100%' : '90vh';
  },
  [dimensions.height, props.isFullScreen, props.withTransition]);

  const width = useMemo(() => {
    if (props.withTransition && dimensions.width) {
      return dimensions.width;
    }

    return props.isFull !== false && props.size !== 'auto' ? '100%' : 'auto';
  },
  [dimensions.width, props.isFull, props.size, props.withTransition]);

  const height = useMemo(() => {
    if (props.withTransition && dimensions.height) {
      return dimensions.height;
    }

    return props.isFullScreen ? '100%' : 'auto';
  },
  [dimensions.height, props.isFullScreen, props.withTransition]);

  useLockBodyScroll();

  const handleClick = (event: React.MouseEvent<HTMLDivElement>) => {
    event.stopPropagation();
  };

  useEffect(() => {
    setIsTransitioning(true);

    const timeout = setTimeout(() => {
      setIsTransitioning(false);
    },
    props.transition);

    return () => {
      clearTimeout(timeout);
    };
  },
  [dimensions, props.transition]);

  return (
    <FocusTrap
      active={active}
      focusTrapOptions={{
        tabbableOptions: {
          displayCheck: 'legacy-full',
        },
      }}
    >
      {/* focus trap requires a single child that accepts a ref */}
      <div className="dialog_container">
        <ClickAwayListener onClickOutside={onClickOutside}>
          <Flex
            alignItems={alignItems}
            backgroundColor="card_background"
            borderRadius={borderRadius}
            flexDirection="column"
            height={height}
            hideScrollBar={isTransitioning}
            maxHeight={maxHeight}
            maxWidth={maxWidth}
            onClick={handleClick}
            opacity={props.opacity}
            overflowY={props.overflowYVisible ? 'visible' : 'auto'}
            position="relative"
            role="dialog"
            top={props.top}
            transition={`${props.transition}ms`}
            width={width}
            zIndex={styles.zIndex.modal}
          >
            {props.withTransition
              ? (
                <Box
                  position="absolute"
                  ref={ref}
                >
                  {props.children}
                </Box>
              )
              : props.children}

            {props.CloseButton}
          </Flex>
        </ClickAwayListener>
      </div>
    </FocusTrap>
  );
};

export default Container;
